import React, { useMemo, useState } from "react";
import { ListBullets, CalendarBlank, CaretLeft, CaretRight, PencilSimple, Trash, WarningDiamond, FunnelSimple, X } from "@phosphor-icons/react";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

export const ENTRY_TYPES = {
  personal: { label: "Personal Use", color: "#7A9E4E" },
  guest: { label: "Guest Use", color: "#CC7351" },
  rental: { label: "Rental", color: "#3557A6" },
};

const DOW = ["Su", "Mo", "Tu", "We", "Th", "Fr", "Sa"];

const toISO = (d) =>
  `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;

const fmtDay = (iso) =>
  new Date(iso + "T00:00:00").toLocaleDateString("en-US", {
    weekday: "short", month: "short", day: "numeric", year: "numeric",
  });

function chipStyle(entry) {
  const color = ENTRY_TYPES[entry.type]?.color || "#888";
  const light = entry.status === "tentative" || entry.status === "available";
  if (light) {
    // open/tentative: clean outline — the status label sits right beside it
    return { backgroundColor: "transparent", border: `1.5px solid ${color}`, color };
  }
  return { backgroundColor: color, border: `1.5px solid ${color}`, color: "#F7F6F2" };
}

export default function CalendarBoard({ entries, canEdit = false, onEdit, onDelete }) {
  const [view, setView] = useState("list");
  const today = new Date();
  const [cursor, setCursor] = useState({ y: today.getFullYear(), m: today.getMonth() });

  // ---- list filters ----
  const [fType, setFType] = useState("all");
  const [fStatus, setFStatus] = useState("all");
  const [fFrom, setFFrom] = useState("");
  const [fTo, setFTo] = useState("");
  const filtersActive = fType !== "all" || fStatus !== "all" || fFrom || fTo;
  const clearFilters = () => {
    setFType("all");
    setFStatus("all");
    setFFrom("");
    setFTo("");
  };

  // date-sorted, then filtered (list view)
  const listEntries = useMemo(() => {
    const sorted = [...(entries || [])].sort((a, b) => a.start_date.localeCompare(b.start_date));
    return sorted.filter((e) => {
      if (fType !== "all" && e.type !== fType) return false;
      if (fStatus !== "all" && e.status !== fStatus) return false;
      if (fFrom && e.end_date < fFrom) return false;
      if (fTo && e.start_date > fTo) return false;
      return true;
    });
  }, [entries, fType, fStatus, fFrom, fTo]);

  const shiftMonth = (delta) => {
    setCursor((c) => {
      const d = new Date(c.y, c.m + delta, 1);
      return { y: d.getFullYear(), m: d.getMonth() };
    });
  };

  // 42 cells covering the visible month
  const cells = useMemo(() => {
    const first = new Date(cursor.y, cursor.m, 1);
    const start = new Date(first);
    start.setDate(first.getDate() - first.getDay());
    return Array.from({ length: 42 }, (_, i) => {
      const d = new Date(start);
      d.setDate(start.getDate() + i);
      return d;
    });
  }, [cursor]);

  const byDay = useMemo(() => {
    const map = {};
    (entries || []).forEach((e) => {
      const s = new Date(e.start_date + "T00:00:00");
      const en = new Date(e.end_date + "T00:00:00");
      for (let d = new Date(s); d <= en; d.setDate(d.getDate() + 1)) {
        const key = toISO(d);
        (map[key] = map[key] || []).push(e);
      }
    });
    return map;
  }, [entries]);

  // entries that truly collide — same-day turnover (out 10 AM, in 4 PM) is fine
  const overlapIds = useMemo(() => {
    const ids = new Set();
    const es = entries || [];
    for (let i = 0; i < es.length; i++) {
      for (let j = i + 1; j < es.length; j++) {
        if (es[i].start_date < es[j].end_date && es[i].end_date > es[j].start_date) {
          ids.add(es[i].id);
          ids.add(es[j].id);
        }
      }
    }
    return ids;
  }, [entries]);

  const monthLabel = new Date(cursor.y, cursor.m, 1).toLocaleDateString("en-US", {
    month: "long", year: "numeric",
  });
  const todayISO = toISO(today);

  return (
    <div data-testid="calendar-board">
      <div className="flex items-center justify-between mb-6">
        <div className="flex gap-2">
          <button
            onClick={() => setView("list")}
            data-testid="view-list-btn"
            className={
              "inline-flex items-center gap-2 px-4 py-2 rounded-full text-sm font-semibold border transition-colors " +
              (view === "list"
                ? "bg-foreground text-primary-foreground border-foreground"
                : "border-border text-muted-foreground hover:text-foreground")
            }
          >
            <ListBullets size={15} weight="bold" /> List
          </button>
          <button
            onClick={() => setView("month")}
            data-testid="view-month-btn"
            className={
              "inline-flex items-center gap-2 px-4 py-2 rounded-full text-sm font-semibold border transition-colors " +
              (view === "month"
                ? "bg-foreground text-primary-foreground border-foreground"
                : "border-border text-muted-foreground hover:text-foreground")
            }
          >
            <CalendarBlank size={15} weight="bold" /> Month
          </button>
        </div>
        <div className="flex flex-wrap gap-3">
          {Object.entries(ENTRY_TYPES).map(([k, t]) => (
            <span key={k} className="inline-flex items-center gap-1.5 text-xs text-muted-foreground" data-testid={`legend-${k}`}>
              <span className="w-3 h-3 rounded-full inline-block" style={{ backgroundColor: t.color }} />
              {t.label}
            </span>
          ))}
        </div>
      </div>

      {view === "list" ? (
        <div>
          <div className="flex flex-wrap items-end gap-x-5 gap-y-3 mb-5" data-testid="calendar-filters">
            <FunnelSimple size={16} className="text-muted-foreground mb-2.5" />
            <div className="space-y-1">
              <label className="block subhead text-foreground/50 text-[10px]">Type</label>
              <Select value={fType} onValueChange={setFType}>
                <SelectTrigger data-testid="filter-type" className="rounded-none border-0 border-b border-border bg-transparent px-0 focus:ring-0 w-32 text-sm h-8">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All types</SelectItem>
                  {Object.entries(ENTRY_TYPES).map(([k, t]) => (
                    <SelectItem key={k} value={k} data-testid={`filter-type-${k}`}>{t.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1">
              <label className="block subhead text-foreground/50 text-[10px]">Status</label>
              <Select value={fStatus} onValueChange={setFStatus}>
                <SelectTrigger data-testid="filter-status" className="rounded-none border-0 border-b border-border bg-transparent px-0 focus:ring-0 w-32 text-sm h-8">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All statuses</SelectItem>
                  <SelectItem value="booked" data-testid="filter-status-booked">Booked</SelectItem>
                  <SelectItem value="available" data-testid="filter-status-available">Available</SelectItem>
                  <SelectItem value="confirmed" data-testid="filter-status-confirmed">Confirmed</SelectItem>
                  <SelectItem value="tentative" data-testid="filter-status-tentative">Tentative</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1">
              <label className="block subhead text-foreground/50 text-[10px]">From</label>
              <input
                type="date"
                value={fFrom}
                onChange={(e) => setFFrom(e.target.value)}
                data-testid="filter-from"
                className="border-0 border-b border-border bg-transparent px-0 text-sm h-8 focus:outline-none focus:border-accent"
              />
            </div>
            <div className="space-y-1">
              <label className="block subhead text-foreground/50 text-[10px]">To</label>
              <input
                type="date"
                value={fTo}
                onChange={(e) => setFTo(e.target.value)}
                data-testid="filter-to"
                className="border-0 border-b border-border bg-transparent px-0 text-sm h-8 focus:outline-none focus:border-accent"
              />
            </div>
            {filtersActive && (
              <button
                onClick={clearFilters}
                data-testid="filter-clear-btn"
                className="inline-flex items-center gap-1.5 text-xs text-accent hover:opacity-70 transition-opacity mb-1.5"
              >
                <X size={13} /> Clear filters
              </button>
            )}
            <span className="text-xs text-muted-foreground mb-1.5 ml-auto" data-testid="filter-count">
              {listEntries.length} of {entries.length}
            </span>
          </div>
        <div className="space-y-px bg-border border border-border" data-testid="calendar-list">
          {listEntries.length === 0 ? (
            <p className="bg-background p-6 text-muted-foreground text-sm">
              {entries.length === 0 ? "No bookings or blocks on the calendar yet." : "Nothing matches these filters."}
            </p>
          ) : (
            listEntries.map((e) => {
              const t = ENTRY_TYPES[e.type] || ENTRY_TYPES.rental;
              return (
                <div key={e.id} className="bg-background p-4 sm:p-5 flex items-start justify-between gap-4" data-testid={`entry-row-${e.id}`}>
                  <div className="min-w-0">
                    <div className="flex flex-wrap items-center gap-2 mb-1.5">
                      <span className="inline-block w-28 text-center truncate text-xs font-semibold px-2.5 py-1 rounded-full" style={chipStyle(e)} data-testid={`entry-type-${e.id}`}>
                        {t.label}
                      </span>
                      {(e.status === "tentative" || e.status === "available") && (
                        <span className="text-[11px] font-semibold uppercase tracking-[0.12em] text-muted-foreground" data-testid={`entry-status-${e.id}`}>
                          {e.status}
                        </span>
                      )}
                      {overlapIds.has(e.id) && (
                        <span
                          className="inline-flex items-center gap-1 text-[11px] font-semibold uppercase tracking-[0.12em] text-amber-700"
                          data-testid={`overlap-warning-${e.id}`}
                          title="This entry's dates overlap another calendar entry"
                        >
                          <WarningDiamond size={14} weight="fill" /> Overlaps
                        </span>
                      )}
                    </div>
                    <p className="text-foreground text-[15px]">
                      <span className="font-semibold">
                        {fmtDay(e.start_date)} <span className="text-muted-foreground font-normal">· 4:00 PM</span>
                        <span className="text-muted-foreground font-normal"> → </span>
                        {fmtDay(e.end_date)} <span className="text-muted-foreground font-normal">· 10:00 AM</span>
                      </span>
                      {e.notes && <span className="text-muted-foreground"> — {e.notes}</span>}
                    </p>
                  </div>
                  {canEdit && (
                    <div className="flex items-center gap-3 shrink-0">
                      <button onClick={() => onEdit?.(e)} data-testid={`entry-edit-${e.id}`} className="text-muted-foreground hover:text-accent transition-colors" title="Edit">
                        <PencilSimple size={17} />
                      </button>
                      <button onClick={() => onDelete?.(e)} data-testid={`entry-delete-${e.id}`} className="text-muted-foreground hover:text-red-700 transition-colors" title="Delete">
                        <Trash size={17} />
                      </button>
                    </div>
                  )}
                </div>
              );
            })
          )}
        </div>
        </div>
      ) : (
        <div className="border border-border bg-background" data-testid="calendar-month">
          <div className="flex items-center justify-between px-5 py-4 border-b border-border">
            <button onClick={() => shiftMonth(-1)} data-testid="cal-prev-btn" className="text-muted-foreground hover:text-foreground transition-colors">
              <CaretLeft size={18} weight="bold" />
            </button>
            <p className="font-display text-xl text-foreground" data-testid="cal-month-label">{monthLabel}</p>
            <button onClick={() => shiftMonth(1)} data-testid="cal-next-btn" className="text-muted-foreground hover:text-foreground transition-colors">
              <CaretRight size={18} weight="bold" />
            </button>
          </div>
          <div className="grid grid-cols-7">
            {DOW.map((d) => (
              <div key={d} className="text-center text-[11px] font-semibold uppercase tracking-[0.12em] text-muted-foreground py-2 border-b border-border">
                {d}
              </div>
            ))}
            {cells.map((d) => {
              const iso = toISO(d);
              const dayEntries = byDay[iso] || [];
              const inMonth = d.getMonth() === cursor.m;
              return (
                <div
                  key={iso}
                  data-testid={`cal-day-${iso}`}
                  className={
                    "min-h-[72px] sm:min-h-[88px] border-b border-r border-border p-1.5 " +
                    (inMonth ? "" : "bg-secondary/40 ") +
                    (iso === todayISO ? "ring-2 ring-inset ring-accent/60 " : "")
                  }
                >
                  <span className={"text-xs " + (inMonth ? "text-foreground" : "text-muted-foreground/50")}>
                    {d.getDate()}
                  </span>
                  <div className="mt-1 space-y-1">
                    {dayEntries.slice(0, 3).map((e) => (
                      <div
                        key={e.id}
                        data-testid={`cal-chip-${e.id}`}
                        className={
                          "text-[10px] font-semibold px-1.5 py-0.5 rounded whitespace-normal leading-tight line-clamp-2" +
                          (canEdit ? " cursor-pointer hover:opacity-80 transition-opacity" : "")
                        }
                        style={chipStyle(e)}
                        onClick={canEdit ? () => onEdit?.(e) : undefined}
                        title={`${ENTRY_TYPES[e.type]?.label || e.type}${(e.status === "tentative" || e.status === "available") ? ` (${e.status})` : ""}${e.notes ? " — " + e.notes : ""}${canEdit ? " — click to edit" : ""}`}
                      >
                        {e.notes || ENTRY_TYPES[e.type]?.label || e.type}
                      </div>
                    ))}
                    {dayEntries.length > 3 && (
                      <span className="text-[10px] text-muted-foreground">+{dayEntries.length - 3} more</span>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}
